import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Input, Checkbox, Button, Typography, Select, Row, Col, Card, Divider, Alert, Tag } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { EntityType, UpdateOperation } from './types';
import { fetchEntities, extractChanges } from './utils';
import SKUConfigEditor from './SKUConfigEditor';
import { apiService } from '../../services/api';
import { useFieldConfig } from '../../contexts/FieldConfigContext';
import { formatEntitlementLabel, noLeadingTrailingSpaces } from '../../utils/utils';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

interface EntityEditorProps {
  entityType: EntityType;
  entityData: Record<string, unknown> | null;
  onUpdate: (update: UpdateOperation) => void;
  filterByProduct?: string;
  alsoUpdateEntitlements?: boolean;
  onAlsoUpdateEntitlementsChange?: (checked: boolean) => void;
  existingUpdates?: UpdateOperation[];
}

const EntityEditor: React.FC<EntityEditorProps> = ({
  entityType,
  entityData,
  onUpdate,
  filterByProduct,
  alsoUpdateEntitlements = false,
  onAlsoUpdateEntitlementsChange,
  existingUpdates = [],
}) => {
  const { isFieldDisabled } = useFieldConfig();
  const [form] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);
  const [availableEntities, setAvailableEntities] = useState<Array<Record<string, unknown>>>([]);
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [productEntitlementCount, setProductEntitlementCount] = useState<number>(0);
  const [productOrgEntitlementCount, setProductOrgEntitlementCount] = useState<number>(0);
  const [availableSKUs, setAvailableSKUs] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedSKUIDs, setSelectedSKUIDs] = useState<number[]>([]);
  const [originalSKUIDs, setOriginalSKUIDs] = useState<number[]>([]);
  const [removedSKUIDs, setRemovedSKUIDs] = useState<number[]>([]);
  const [savedSelectedSKUIDs, setSavedSelectedSKUIDs] = useState<number[]>([]);
  const [savedRemovedSKUIDs, setSavedRemovedSKUIDs] = useState<number[]>([]);
  const [requireDescription, setRequireDescription] = useState(true);

  // Memoize selectedSKU to prevent duplicate API calls in SKUConfigEditor
  const selectedSKU = useMemo(() => {
    if (entityData) {
      return { id: entityData.id as number, name: entityData.name as string };
    }
    return null;
  }, [entityData]);

  const loadRelatedEntities = useCallback(async () => {
    try {
      const entities = await fetchEntities(entityType);
      const filtered = filterByProduct
        ? (entities as Array<Record<string, unknown>>).filter((e) => {
          const productName = e.product_name;
          return productName === filterByProduct;
        })
        : entities;
      setAvailableEntities(filtered as Array<Record<string, unknown>>);
    } catch (error) {
      // Error handled by parent
    }
  }, [entityType, filterByProduct]);

  useEffect(() => {
    if (entityData) {
      // Find matching pending update for this entity to restore edited values
      const pendingUpdate = existingUpdates.find(
        (u) => u.entityType === entityType && u.entityId === String(entityData.id ?? entityData.ID),
      );

      // Derive effective description: pending update overrides original
      const effectiveDescription = pendingUpdate?.updates?.description ?? entityData.description;
      const hasDescription = typeof effectiveDescription === 'string'
        ? effectiveDescription.trim().length > 0
        : Boolean(effectiveDescription);

      if (pendingUpdate) {
        form.setFieldsValue({ requireDescription: hasDescription, ...entityData, ...pendingUpdate.updates });
      } else {
        form.setFieldsValue({ requireDescription: hasDescription, ...entityData });
      }
      setRequireDescription(hasDescription);
    } else if (filterByProduct) {
      void loadRelatedEntities();
    }
  }, [entityData, filterByProduct, loadRelatedEntities, form, existingUpdates, entityType]);

  // Load entitlement counts for Product entity type
  useEffect(() => {
    if (entityType === EntityType.PRODUCT && entityData) {
      const productId = Number(entityData.id || entityData.ID);
      if (productId) {
        const loadEntitlementCounts = async () => {
          try {
            const [ents, orgEnts] = await Promise.all([
              (await apiService.getEntitlements()).filter((e) => e.product_id === productId),
              (await apiService.getOrgEntitlements()).filter((oe) => oe.product_id === productId),
            ]);
            setProductEntitlementCount(ents.length);
            setProductOrgEntitlementCount(orgEnts.length);
          } catch {
            setProductEntitlementCount(0);
            setProductOrgEntitlementCount(0);
          }
        };
        void loadEntitlementCounts();
      }
    }
  }, [entityType, entityData]);

  // Load SKUs and their org entitlement mappings for DEPENDENT_SERVICE
  useEffect(() => {
    if (entityType === EntityType.DEPENDENT_SERVICE && entityData) {
      const orgEntitlementId = Number(entityData.id || entityData.ID);
      if (orgEntitlementId) {
        const loadSKUsForOrgEntitlement = async () => {
          try {
            const [allSKUs, skuOrgConfigs] = await Promise.all([
              apiService.getSKUs(),
              apiService.getSKUOrgEntitlementConfigs(),
            ]);

            setAvailableSKUs(allSKUs.map((sku) => ({ id: sku.id ?? 0, name: sku.name })));

            // Filter configs that match this org entitlement
            const matchedSKUIDs = skuOrgConfigs
              .filter((config) => config.org_entitlement_id === orgEntitlementId)
              .map((config) => config.sku_id);

            // Check if there's a pending update to restore
            const pendingUpdate = existingUpdates.find(
              (u) => u.entityType === entityType && u.entityId === String(entityData.id ?? entityData.ID),
            );

            if (pendingUpdate) {
              // Restore pending changes when coming back from review
              const addedIDs = (pendingUpdate.updates.added_sku_ids || []) as number[];
              const removedIDs = (pendingUpdate.updates.removed_sku_ids || []) as number[];

              // Calculate current selected SKUs (original + added - removed)
              const currentSelectedSKUIDs = matchedSKUIDs
                .filter((id) => !removedIDs.includes(id))
                .concat(addedIDs.filter((id) => !matchedSKUIDs.includes(id)));

              setSelectedSKUIDs(currentSelectedSKUIDs);
              setOriginalSKUIDs(matchedSKUIDs);
              setRemovedSKUIDs(removedIDs);

              // Set saved state to current restored state (already saved)
              setSavedSelectedSKUIDs(currentSelectedSKUIDs);
              setSavedRemovedSKUIDs(removedIDs);

              form.setFieldsValue({ sku_ids: currentSelectedSKUIDs });

              // No unsaved changes since we're restoring previously saved state
              setHasChanges(false);
            } else {
              // No pending update, use original data
              setSelectedSKUIDs(matchedSKUIDs);
              setOriginalSKUIDs(matchedSKUIDs);
              setRemovedSKUIDs([]);

              // Initialize saved state as original state
              setSavedSelectedSKUIDs(matchedSKUIDs);
              setSavedRemovedSKUIDs([]);

              form.setFieldsValue({ sku_ids: matchedSKUIDs });
            }
          } catch {
            setAvailableSKUs([]);
            setSelectedSKUIDs([]);
          }
        };
        void loadSKUsForOrgEntitlement();
      }
    }
  }, [entityType, entityData, form, existingUpdates]);

  const handleFormChange = useCallback(() => {
    if (entityData) {
      // Sync requireDescription state from form
      setRequireDescription(form.getFieldValue('requireDescription') !== false);

      // For DEPENDENT_SERVICE, check SKU changes against saved state
      if (entityType === EntityType.DEPENDENT_SERVICE) {
        // Check if current state differs from saved state
        const currentSelectedString = JSON.stringify([...selectedSKUIDs].sort());
        const savedSelectedString = JSON.stringify([...savedSelectedSKUIDs].sort());
        const currentRemovedString = JSON.stringify([...removedSKUIDs].sort());
        const savedRemovedString = JSON.stringify([...savedRemovedSKUIDs].sort());

        const skuChanged = currentSelectedString !== savedSelectedString || currentRemovedString !== savedRemovedString;
        setHasChanges(skuChanged);
      } else {
        // Validate first — Save is only enabled when all rules pass AND there are changes
        // setTimeout(0) ensures form store has settled before we validate
        setTimeout(() => {
          form.validateFields()
            .then(() => {
              const formData = form.getFieldsValue();
              const changes = extractChanges(entityData, formData);
              setHasChanges(Object.keys(changes).length > 0 || alsoUpdateEntitlements);
            })
            .catch(() => {
              // Validation failed — disable Save
              setHasChanges(false);
            });
        }, 0);
      }
    }
  }, [entityData, entityType, selectedSKUIDs, savedSelectedSKUIDs, removedSKUIDs, savedRemovedSKUIDs, form, alsoUpdateEntitlements]);

  // Trigger form change check when alsoUpdateEntitlements changes
  useEffect(() => {
    handleFormChange();
  }, [alsoUpdateEntitlements, handleFormChange]);

  const handleSave = () => {
    if (!entityData) {
      return;
    }

    // Special handling for DEPENDENT_SERVICE to track SKU changes (no form validation needed)
    if (entityType === EntityType.DEPENDENT_SERVICE) {
      const addedSKUIDs = selectedSKUIDs.filter((id) => !originalSKUIDs.includes(id));

      if (addedSKUIDs.length === 0 && removedSKUIDs.length === 0) {
        return; // No changes
      }

      const entityId = getEntityId(entityData);
      const entityName = getEntityName(entityData);
      const orgEntitlementId = Number(entityData.id || entityData.ID);

      const update: UpdateOperation = {
        entityType,
        entityId,
        entityName,
        updates: {
          added_sku_ids: addedSKUIDs,
          removed_sku_ids: removedSKUIDs,
          org_entitlement_id: orgEntitlementId,
        },
        originalData: entityData,
      };

      onUpdate(update);

      // Update saved state to current state
      setSavedSelectedSKUIDs(selectedSKUIDs);
      setSavedRemovedSKUIDs(removedSKUIDs);
      setHasChanges(false);
      return;
    }

    // Validate all fields before saving
    form.validateFields()
      .then((formData) => {
        const changes = extractChanges(entityData, formData as Record<string, unknown>);
        if (Object.keys(changes).length === 0) {
          return;
        }

        // Strip UI-only field — not part of the API payload
        delete changes.requireDescription;

        const entityId = getEntityId(entityData);
        const entityName = getEntityName(entityData);

        const update: UpdateOperation = {
          entityType,
          entityId,
          entityName,
          updates: changes,
          originalData: entityData,
        };

        onUpdate(update);
        setHasChanges(false);
      })
      .catch(() => {
        // Validation failed — form will display inline errors, keep Save disabled
        setHasChanges(false);
      });
  };

  const handleSaveRelatedEntities = () => {
    selectedEntities.forEach((entityId) => {
      const entity = availableEntities.find((e) => getEntityId(e) === entityId);
      if (entity) {
        const update: UpdateOperation = {
          entityType,
          entityId: getEntityId(entity),
          entityName: getEntityName(entity),
          updates: entity,
          originalData: entity,
        };
        onUpdate(update);
      }
    });
    setSelectedEntities([]);
  };

  const getEntityId = (data: Record<string, unknown>): string => {
    switch (entityType) {
      case EntityType.PRODUCT:
        return String(data.id || data.ID || data.product_id || '');
      case EntityType.ENTITLEMENT:
        return String(data.id || data.ID || data.entitlement_id || '');
      case EntityType.ORG_ENTITLEMENT:
      case EntityType.DEPENDENT_SERVICE:
        return String(data.id || data.ID || data.org_entitlement_id || '');
      case EntityType.SKU_STANDALONE:
      case EntityType.SKU_SUITE:
      case EntityType.SKU_ADDON:
        return String(data.id || data.ID || data.sku_id || '');
      default:
        return '';
    }
  };

  const getEntityName = (data: Record<string, unknown>): string => {
    switch (entityType) {
      case EntityType.PRODUCT:
        return String(data.name || data.product_name || '');
      case EntityType.ENTITLEMENT:
        return "rtst"+formatEntitlementLabel({
          name: String(data.name || data.entitlement_name || ''),
          value: String(data.value || ''),
          description: data.description ? String(data.description) : undefined,
          needs_quantity: Boolean(data.needs_quantity),
          unit: data.unit ? String(data.unit) : undefined,
        });
      case EntityType.ORG_ENTITLEMENT:
      case EntityType.DEPENDENT_SERVICE:
        return formatEntitlementLabel({
          name: String(data.name || data.org_entitlement_name || ''),
          value: String(data.value || ''),
          description: data.description ? String(data.description) : undefined,
          needs_quantity: Boolean(data.needs_quantity),
          unit: data.unit ? String(data.unit) : undefined,
        });
      case EntityType.SKU_STANDALONE:
      case EntityType.SKU_SUITE:
      case EntityType.SKU_ADDON:
        return String(data.sku_name || data.name || '');
      default:
        return '';
    }
  };

  const renderFields = () => {
    switch (entityType) {
      case EntityType.PRODUCT:
        return (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Product Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter product name' }, noLeadingTrailingSpaces]}
                >
                  <Input placeholder="e.g., Cisco XDR, SASE" disabled={isFieldDisabled(entityType, 'name')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Product Key"
                  name="key"
                  rules={[{ required: true, message: 'Please enter product key' }, noLeadingTrailingSpaces]}
                  extra="Lowercase identifier for the product"
                >
                  <Input placeholder="e.g., xdr, sase" disabled={isFieldDisabled(entityType, 'key')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Provisioning Client"
                  name="provisioning_client"
                  extra="The client responsible for provisioning"
                >
                  <Select placeholder="Select provisioning client" disabled={isFieldDisabled(entityType, 'provisioning_client')}>
                    <Option value="CLIENT_COMMON">CLIENT_COMMON</Option>
                    <Option value="CLIENT_KIT">CLIENT_KIT</Option>
                    <Option value="CLIENT_PLATFORM">CLIENT_PLATFORM</Option>
                    <Option value="CLIENT_XDR">CLIENT_XDR</Option>
                    <Option value="CLIENT_SASE">CLIENT_SASE</Option>
                    <Option value="CLIENT_AI_DEFENSE">CLIENT_AI_DEFENSE</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Provisioning Style"
                  name="provisioning_style"
                  extra="Synchronous or asynchronous provisioning"
                >
                  <Select placeholder="Select provisioning style">
                    <Option value="STYLE_SYNC">STYLE_SYNC</Option>
                    <Option value="STYLE_ASYNC">STYLE_ASYNC</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Card
              title="Product Flags"
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Form.Item
                    label="Is Hidden"
                    name="is_hidden"
                    valuePropName="checked"
                    tooltip="Hide product from user interface"
                  >
                    <Checkbox disabled={isFieldDisabled(entityType, 'is_hidden')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Auto Provision"
                    name="is_auto_provision"
                    valuePropName="checked"
                    tooltip="Automatically provision when added"
                  >
                    <Checkbox disabled={isFieldDisabled(entityType, 'is_auto_provision')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Composite Product"
                    name="is_composite"
                    valuePropName="checked"
                    tooltip="Product composed of multiple sub-products"
                  >
                    <Checkbox disabled={isFieldDisabled(entityType, 'is_composite')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Brownfield Only"
                    name="is_brownfield_only"
                    valuePropName="checked"
                    tooltip="Only for existing deployments"
                  >
                    <Checkbox disabled={isFieldDisabled(entityType, 'is_brownfield_only')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Unified Onboarding"
                    name="is_unified_onboarding_supported"
                    valuePropName="checked"
                    tooltip="Supports unified onboarding flow"
                  >
                    <Checkbox disabled={isFieldDisabled(entityType, 'is_unified_onboarding_supported')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Parent Provisioning"
                    name="is_parent_provisioning_supported"
                    valuePropName="checked"
                    tooltip="Supports parent-level provisioning"
                  >
                    <Checkbox disabled={isFieldDisabled(entityType, 'is_parent_provisioning_supported')} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Inherit Parent Metadata"
                    name="inherit_parent_metadata"
                    valuePropName="checked"
                    tooltip="Inherit metadata from parent product"
                  >
                    <Checkbox disabled={isFieldDisabled(entityType, 'inherit_parent_metadata')} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Divider />

            <Title level={5} style={{ marginBottom: 12 }}>Additional Options</Title>
            <Checkbox
              checked={alsoUpdateEntitlements}
              onChange={(e) => onAlsoUpdateEntitlementsChange?.(e.target.checked)}
            >
              Also update product entitlements
            </Checkbox>

            {(productEntitlementCount > 0 || productOrgEntitlementCount > 0) ? (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                ℹ️ This product has {productEntitlementCount > 0 && productOrgEntitlementCount > 0
                  ? `${productEntitlementCount} standard and ${productOrgEntitlementCount} org`
                  : productOrgEntitlementCount > 0
                    ? `${productOrgEntitlementCount} org`
                    : `${productEntitlementCount} standard`
                } entitlements. Check the box above to modify them.
              </Text>
            ) : (
              <Text type="warning" style={{ display: 'block', marginTop: 8 }}>
                ⚠️ No entitlements found for this product.
              </Text>
            )}
          </>
        );

      case EntityType.ENTITLEMENT:
        return (
          <>
            {!requireDescription && (
              <Alert
                message="⚠️ No description — this entitlement will not be displayed on SCC UI"
                type="warning"
                style={{ marginBottom: 16 }}
                showIcon={false}
              />
            )}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Entitlement Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter entitlement name' }, noLeadingTrailingSpaces]}
                >
                  <Input placeholder="e.g., premium_tier, advanced_features" disabled={isFieldDisabled(entityType, 'name')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Entitlement Value"
                  name="value"
                  extra="The value associated with this entitlement"
                  rules={[noLeadingTrailingSpaces]}
                >
                  <Input placeholder="e.g., premium, 100, enabled" disabled={isFieldDisabled(entityType, 'value')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Entitlement Unit"
                  name="unit"
                  extra="Unit of measurement (e.g., users, GB)"
                  rules={[noLeadingTrailingSpaces]}
                >
                  <Input placeholder="e.g., users, GB, devices" disabled={isFieldDisabled(entityType, 'unit')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Entitlement Description"
                  name="description"
                  rules={requireDescription ? [{ required: true, message: 'Please enter a description or uncheck "Require Description"' }, noLeadingTrailingSpaces] : [noLeadingTrailingSpaces]}
                >
                  <TextArea rows={3} placeholder="Describe what this entitlement provides" disabled={isFieldDisabled(entityType, 'description')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Require Description"
                  name="requireDescription"
                  valuePropName="checked"
                  tooltip="Uncheck to allow submission without a description. Note: entitlements without a description will not appear on SCC UI."
                >
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          </>
        );

      case EntityType.ORG_ENTITLEMENT:
        return (
          <>
            {!requireDescription && (
              <Alert
                message="⚠️ No description — this entitlement will not be displayed on SCC UI"
                type="warning"
                style={{ marginBottom: 16 }}
                showIcon={false}
              />
            )}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Org Entitlement Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter org entitlement name' }, noLeadingTrailingSpaces]}
                >
                  <Input placeholder="e.g., max_users, storage_limit" disabled={isFieldDisabled(entityType, 'name')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Org Entitlement Value"
                  name="value"
                  extra="The value for this organization entitlement"
                  rules={[noLeadingTrailingSpaces]}
                >
                  <Input placeholder="e.g., 1000, unlimited" disabled={isFieldDisabled(entityType, 'value')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Org Entitlement Unit"
                  name="unit"
                  extra="Unit of measurement (e.g., users, TB)"
                  rules={[noLeadingTrailingSpaces]}
                >
                  <Input placeholder="e.g., users, TB, licenses" disabled={isFieldDisabled(entityType, 'unit')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Org Entitlement Description"
                  name="description"
                  rules={requireDescription ? [{ required: true, message: 'Please enter a description or uncheck "Require Description"' }, noLeadingTrailingSpaces] : [noLeadingTrailingSpaces]}
                >
                  <TextArea rows={3} placeholder="Describe this organization-level entitlement" disabled={isFieldDisabled(entityType, 'description')} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Require Description"
                  name="requireDescription"
                  valuePropName="checked"
                  tooltip="Uncheck to allow submission without a description. Note: entitlements without a description will not appear on SCC UI."
                >
                  <Checkbox />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Is Service"
                  name="is_service"
                  valuePropName="checked"
                  extra="Mark this org entitlement as a service-enabled entitlement"
                >
                  <Checkbox disabled={isFieldDisabled(entityType, 'is_service')} />
                </Form.Item>
              </Col>
            </Row>
          </>
        );

      case EntityType.DEPENDENT_SERVICE:
        return (
          <>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Service Org Entitlement Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter org entitlement name' }, noLeadingTrailingSpaces]}
                >
                  <Input disabled={isFieldDisabled(entityType, 'name')} placeholder="Service org entitlement name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Value"
                  name="value"
                >
                  <Input disabled placeholder="Entitlement value" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Description"
                  name="description"
                >
                  <Input disabled placeholder="Description" />
                </Form.Item>
              </Col>
            </Row>

            <Alert
              message={`This org entitlement has ${selectedSKUIDs.length} SKU${selectedSKUIDs.length !== 1 ? 's' : ''} linked`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Divider>Linked SKUs</Divider>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Associated SKUs"
                  name="sku_ids"
                  extra="Green = newly added, Red = will be removed, Blue = existing"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select SKUs to link with this service"
                    value={selectedSKUIDs}
                    onChange={(values: number[]) => {
                      // Determine which SKUs were removed from the Select
                      const clickedToRemove = selectedSKUIDs.filter(id => !values.includes(id));

                      let newSelectedSKUIDs = selectedSKUIDs;
                      let newRemovedSKUIDs = removedSKUIDs;

                      if (clickedToRemove.length > 0) {
                        // User clicked X on a tag
                        const clickedId = clickedToRemove[0];
                        if (originalSKUIDs.includes(clickedId)) {
                          // If it's an original SKU, mark as removed (show as red tag below)
                          newSelectedSKUIDs = selectedSKUIDs.filter(id => id !== clickedId);
                          newRemovedSKUIDs = [...removedSKUIDs, clickedId];
                          setSelectedSKUIDs(newSelectedSKUIDs);
                          setRemovedSKUIDs(newRemovedSKUIDs);
                        } else {
                          // If it's a newly added SKU, just remove it completely
                          newSelectedSKUIDs = selectedSKUIDs.filter(id => id !== clickedId);
                          setSelectedSKUIDs(newSelectedSKUIDs);
                        }
                      } else {
                        // User added a new SKU from dropdown
                        const addedIds = values.filter(id => !selectedSKUIDs.includes(id));
                        addedIds.forEach(addedId => {
                          if (removedSKUIDs.includes(addedId)) {
                            // Re-adding a removed SKU
                            newRemovedSKUIDs = removedSKUIDs.filter(id => id !== addedId);
                            setRemovedSKUIDs(newRemovedSKUIDs);
                          }
                        });
                        newSelectedSKUIDs = values;
                        setSelectedSKUIDs(values);
                      }

                      form.setFieldsValue({ sku_ids: values });

                      // Check changes using the new values (not waiting for state update)
                      const addedSKUIDs = newSelectedSKUIDs.filter((id) => !originalSKUIDs.includes(id));
                      const hasChanges = addedSKUIDs.length > 0 || newRemovedSKUIDs.length > 0;
                      setHasChanges(hasChanges);
                    }}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children?.toString() || '').toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                    tagRender={(props) => {
                      const { label, value, closable, onClose } = props;
                      const skuId = Number(value);
                      const isNew = !originalSKUIDs.includes(skuId);

                      const color = isNew ? 'green' : 'blue'; // newly added or existing

                      return (
                        <Tag
                          color={color}
                          closable={closable}
                          onClose={onClose}
                          style={{ marginRight: 3 }}
                        >
                          {label}
                        </Tag>
                      );
                    }}
                  >
                    {availableSKUs.map((sku) => (
                      <Option key={sku.id} value={sku.id}>
                        {sku.name}
                      </Option>
                    ))}
                  </Select>
                  {removedSKUIDs.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      {removedSKUIDs.map((skuId) => {
                        const sku = availableSKUs.find(s => s.id === skuId);
                        return (
                          <Tag
                            key={skuId}
                            color="red"
                            closable
                            onClose={() => {
                              // Re-add the SKU by moving it back to selected
                              const newRemovedSKUIDs = removedSKUIDs.filter(id => id !== skuId);
                              const newSelectedSKUIDs = [...selectedSKUIDs, skuId];
                              setRemovedSKUIDs(newRemovedSKUIDs);
                              setSelectedSKUIDs(newSelectedSKUIDs);
                              form.setFieldsValue({ sku_ids: newSelectedSKUIDs });

                              // Check changes using the new values (not waiting for state update)
                              const addedSKUIDs = newSelectedSKUIDs.filter((id) => !originalSKUIDs.includes(id));
                              const hasChanges = addedSKUIDs.length > 0 || newRemovedSKUIDs.length > 0;
                              setHasChanges(hasChanges);
                            }}
                            style={{ marginRight: 3, marginBottom: 4 }}
                          >
                            {sku?.name || `SKU ${skuId}`}
                          </Tag>
                        );
                      })}
                    </div>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </>
        );

      case EntityType.SKU_STANDALONE:
      case EntityType.SKU_SUITE:
      case EntityType.SKU_ADDON:
        // SKU configuration is handled by SKUConfigEditor component
        // Don't render a form here, return null
        return null;

      default:
        return null;
    }
  };

  if (!entityData && !filterByProduct) {
    return <Text type="secondary">Please select an entity from the previous step.</Text>;
  }

  // Special handling for SKU entity types - show SKUConfigEditor
  if (entityType === EntityType.SKU_STANDALONE ||
      entityType === EntityType.SKU_SUITE ||
      entityType === EntityType.SKU_ADDON) {
    if (selectedSKU && entityData) {
      return (
        <SKUConfigEditor
          selectedSKU={selectedSKU}
          entityType={entityType}
          onConfigChange={(changes) => {
            // Create an update operation with SKU config changes
            const update: UpdateOperation = {
              entityType,
              entityId: String(entityData.id),
              entityName: String(entityData.name),
              updates: changes,
              originalData: entityData,
            };
            onUpdate(update);
          }}
        />
      );
    }
  }

  if (filterByProduct) {
    return (
      <div>
        <Text>
          Select {entityType === EntityType.ENTITLEMENT ? 'entitlements' : 'org entitlements'} to update:
        </Text>
        <Select
          mode="multiple"
          style={{ width: '100%', marginTop: 8, marginBottom: 16 }}
          placeholder="Select items"
          value={selectedEntities}
          onChange={setSelectedEntities}
        >
          {availableEntities.map((entity) => (
            <Option key={getEntityId(entity)} value={getEntityId(entity)}>
              {getEntityName(entity)}
            </Option>
          ))}
        </Select>
        <Button
          type="primary"
          onClick={handleSaveRelatedEntities}
          disabled={selectedEntities.length === 0}
        >
          Add Selected for Update
        </Button>
      </div>
    );
  }

  return (
    <Card title={`Edit ${entityType.replace('_', ' ').toUpperCase()}`} bordered={false}>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        style={{ maxWidth: 1200 }}
      >
        {renderFields()}

        <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            disabled={!hasChanges}
            size="large"
          >
            Save Changes
          </Button>
          {hasChanges && (
            <Text type="secondary" style={{ marginLeft: 16 }}>
              You have unsaved changes
            </Text>
          )}
        </Form.Item>
      </Form>
    </Card>
  );
};

export default EntityEditor;
